import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { ArrowLeft, Users, Plus, Building2, ArrowRight } from 'lucide-react';

const STATUS_COLORS = {
  new: 'bg-gray-100 text-gray-700',
  assigned: 'bg-blue-100 text-blue-700',
  contacted: 'bg-cyan-100 text-cyan-700',
  under_review: 'bg-amber-100 text-amber-700',
  candidate_search: 'bg-purple-100 text-purple-700',
  interviews: 'bg-indigo-100 text-indigo-700',
  offer_sent: 'bg-pink-100 text-pink-700',
  placement_completed: 'bg-emerald-100 text-emerald-700',
  closed: 'bg-slate-100 text-slate-700',
  cancelled: 'bg-red-100 text-red-700',
};

export default function MyRecruitmentRequestsPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const res = await api.get('/recruitment/requests');
      setRequests(res.data || []);
    } catch {
      toast.error('Failed to load your employment requests');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F0F4F8]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="w-4 h-4" />Back
        </button>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">My Employment Requests</h1>
            <p className="text-muted-foreground mt-1">Track your Direct Employment Solutions requests</p>
          </div>
          <Link to="/employer/direct-recruitment">
            <Button className="bg-[#003366] hover:bg-[#003366]/90">
              <Plus className="w-4 h-4 mr-2" />New Request
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="space-y-4">{Array(3).fill(0).map((_, i) => <Skeleton key={i} className="h-32" />)}</div>
        ) : requests.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold text-lg mb-2">No requests yet</h3>
              <p className="text-muted-foreground mb-6">Submit a Direct Employment Solutions request to get started.</p>
              <Link to="/employer/direct-recruitment">
                <Button className="bg-[#003366] hover:bg-[#003366]/90">Submit Employment Request</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {requests.map((req) => (
              <Card key={req.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/employer/recruitment-requests/${req.id}`)}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#003366]/10 flex items-center justify-center shrink-0">
                        <Building2 className="w-5 h-5 text-[#003366]" />
                      </div>
                      <div>
                        <p className="font-semibold">{req.job_title}</p>
                        <p className="text-sm text-muted-foreground">{req.location} • {req.positions_count} position(s)</p>
                        <p className="text-xs text-muted-foreground font-mono mt-1">{req.reference_code}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={STATUS_COLORS[req.status] || 'bg-gray-100 text-gray-700'}>
                        {(req.status || '').replace(/_/g, ' ')}
                      </Badge>
                      <ArrowRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
